
import { AutoLinkNode } from '@lexical/link';
import { AutoFocusPlugin } from '@lexical/react/LexicalAutoFocusPlugin';
import { AutoLinkPlugin } from '@lexical/react/LexicalAutoLinkPlugin.js';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { $getRoot } from 'lexical';
import EmojiPlugin from './EmojiPlugin';
import './RicherText.scss';
import React from 'react';
import InitialValuePlugin from './InitialValuePlugin';
import ResetOnSignalPlugin from './ResetOnSignalPlugin.jsx';

const theme = {
  ltr: 'ltr',
  rtl: 'rtl',
  paragraph: 'editor-paragraph'
}

const URL_MATCHER =
  /((https?:\/\/(www\.)?)|(www\.))[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)/;

const MATCHERS = [
  (text) => {
    const match = URL_MATCHER.exec(text);
    if (match === null) {
      return null;
    }
    const fullMatch = match[0];
    return {
      index: match.index,
      length: fullMatch.length,
      text: fullMatch,
      url: fullMatch.startsWith('http') ? fullMatch : `https://${fullMatch}`,
      // attributes: { rel: 'noreferrer', target: '_blank' }, // Optional link attributes
    };
  },
];

// Catch lexical errors. 
function onError(error) {
  console.error(error);
}

const RicherTextArea = React.memo(({ onChange, EditorStyle, EmojiPickerDirection = "bottom", initialValue = "", resetSignal}) => {
  const initialConfig = {
    namespace: 'CreatePostEditor',
    theme,
    onError,
    nodes: [
      AutoLinkNode
    ]
  };

  return (
    <LexicalComposer initialConfig={initialConfig}>
      <AutoLinkPlugin matchers={MATCHERS} />
      <div style={{ position: "relative" }}>
        <RichTextPlugin
          contentEditable={
            <ContentEditable
              className="editor-content"
              aria-placeholder="Enter some text..."
              style={EditorStyle}
            />
          }
          placeholder={<div className="editor-placeholder">Enter some text...</div>}
          ErrorBoundary={LexicalErrorBoundary}
        />
      </div>

      {/* Will populate when empty (or always, if you pass overwriteOnChange) */}
      <InitialValuePlugin value={initialValue} />

      <OnChangePlugin
        onChange={(editorState) => {
          editorState.read(() => {
            const text = $getRoot().getTextContent();
            onChange(text);
          });
        }}
      />
      <ResetOnSignalPlugin resetSignal={resetSignal} />
      <HistoryPlugin />
      {/* <AutoFocusPlugin />  */} {/* Uncommented to prevent autofocus on capacitor bundles */}
      <EmojiPlugin pickerDirection={EmojiPickerDirection} />
    </LexicalComposer>

  );
}, (prev, next) => {
  return (
    prev.resetSignal === next.resetSignal &&     // must flip to re-render
    prev.initialValue === next.initialValue &&
    prev.EmojiPickerDirection === next.EmojiPickerDirection &&
    prev.onChange === next.onChange &&
    prev.EditorStyle === next.EditorStyle
  );
});

RicherTextArea.whyDidYouRender = true;

export default RicherTextArea;